import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faGripVertical } from "@fortawesome/pro-solid-svg-icons/faGripVertical";
import { faStar } from "@fortawesome/pro-solid-svg-icons/faStar";
import { faTrash } from "@fortawesome/pro-solid-svg-icons/faTrash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "antd/es/col";
import Form, { FormInstance } from "antd/es/form";
import Row from "antd/es/row";
import Select from "antd/es/select";
import Space from "antd/es/space";
import cn from "classnames";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { useIntl } from "react-intl";

import CreateDealLossReasonPayload from "@mapmycustomers/shared/types/entity/deals/CreateDealLossReasonPayload";
import DealLossReason from "@mapmycustomers/shared/types/entity/deals/DealLossReason";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button, Popconfirm, SelectField, TextField } from "@mapmycustomers/ui";

import { getFunnels } from "@app/store/deal";
import { deleteDealLossReason } from "@app/store/dealLossReasons/actions";
import { getFavoriteFunnels } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import sortFunnelList from "@app/util/sortFunnelList";

import LossReasonsFormValue from "../../type/LossReasonsFormValue";

import styles from "./LossReasonsList.module.scss";

interface Props {
  deleteDealLossReason: typeof deleteDealLossReason.request;
  favoriteFunnelsIds: Funnel["id"][];
  form: FormInstance<LossReasonsFormValue>;
  funnels: Funnel[];
  lossReasons: DealLossReason[];
  onDrop: (lossReason: DealLossReason[]) => void;
  onValidate: (valid: boolean) => void;
}

const LossReasonsList: React.FC<Props> = ({
  deleteDealLossReason,
  favoriteFunnelsIds,
  form,
  funnels,
  lossReasons,
  onDrop,
  onValidate,
}) => {
  const intl = useIntl();

  const [dragging, setDragging, unsetDragging] = useBoolean();

  const onDragEnd = useCallback(
    ({ destination, source }: DropResult) => {
      unsetDragging();

      if (!destination) {
        return;
      }

      const sourceIndex = source.index;
      const destinationIndex = destination.index;

      const lossReasonsList = form.getFieldValue("lossReasons");
      let updatedLossReasonsList = [
        ...lossReasonsList.slice(0, sourceIndex),
        ...lossReasonsList.slice(sourceIndex + 1),
      ];
      updatedLossReasonsList.splice(destinationIndex, 0, lossReasonsList[sourceIndex]);
      updatedLossReasonsList = updatedLossReasonsList.map((reason, index) => ({
        ...reason,
        displayOrder: index,
      }));
      form.setFieldsValue({ lossReasons: updatedLossReasonsList });
      onDrop(updatedLossReasonsList);
    },
    [form, onDrop, unsetDragging]
  );

  const handleDeleteLossReason = useCallback(
    (index) => {
      const lossReasons = form.getFieldValue("lossReasons");
      if (lossReasons[index]?.id) {
        deleteDealLossReason(lossReasons[index].id);
      }
      if (!lossReasons[index].id) {
        form.setFieldsValue({
          lossReasons: [...lossReasons.slice(0, index), ...lossReasons.slice(index + 1)],
        });
      }
    },
    [deleteDealLossReason, form]
  );

  const handleAddLossReason = useCallback(() => {
    const lossReasons = form.getFieldValue("lossReasons");
    form.setFieldsValue({
      lossReasons: [
        ...lossReasons,
        {
          displayOrder: -1,
          funnelId: [] as Funnel["id"][],
          name: "",
        } as CreateDealLossReasonPayload,
      ],
    });
  }, [form]);

  const sortedFunnels = useMemo(
    () => sortFunnelList(funnels, favoriteFunnelsIds),
    [favoriteFunnelsIds, funnels]
  );

  const funnelField = useCallback(
    (index: number) => {
      const lossReasons = form.getFieldValue("lossReasons");
      const funnelIds = lossReasons[index].funnelId || [];

      return (
        <SelectField<Funnel["id"][]>
          allowClear
          className={styles.funnelSelect}
          dropdownMatchSelectWidth={false}
          maxTagCount="responsive"
          mode="multiple"
          placeholder={
            funnelIds.length
              ? undefined
              : intl.formatMessage({
                  id: "deals.setting.dealLostReasonModal.allFunnels",
                  defaultMessage: "All Funnels",
                  description: "All funnels in a deal loss reason selection field",
                })
          }
          value={funnelIds}
        >
          {sortedFunnels.map((funnel) => (
            <Select.Option className={styles.option} key={funnel.id} value={funnel.id}>
              <p className={styles.optionTitle}>{funnel.name}</p>
              {favoriteFunnelsIds.includes(funnel.id) && (
                <FontAwesomeIcon className={styles.starIcon} icon={faStar} />
              )}
            </Select.Option>
          ))}
        </SelectField>
      );
    },
    [sortedFunnels, favoriteFunnelsIds, form, intl]
  );

  const handleFormValuesChange = useCallback(() => {
    form.validateFields().catch((rejectionReason) => {
      onValidate(rejectionReason.errorFields?.length === 0);
    });
  }, [form, onValidate]);

  const getReasonNameRules = (lossReasons: DealLossReason[], reasonFieldIndex: number) => [
    () => ({
      validator: (_: any, value: string) => {
        const dealLossReasonExists = lossReasons.find(
          (reason: CreateDealLossReasonPayload | DealLossReason, reasonIndex: number) =>
            reason.name === value && reasonIndex !== reasonFieldIndex
        );

        if (value && !dealLossReasonExists) {
          return Promise.resolve();
        }

        return Promise.reject(
          new Error(
            intl.formatMessage({
              id: "deals.setting.dealLostReasonModal.name.error.empty",
              defaultMessage: "Please enter a unique reason name",
              description:
                "Error message for uniqueness for deal loss reason name validation rule in Deal Loss Reason modal",
            })
          )
        );
      },
    }),
  ];

  return (
    <DragDropContext onBeforeDragStart={() => setDragging()} onDragEnd={onDragEnd}>
      <Form form={form} onValuesChange={handleFormValuesChange}>
        <Droppable droppableId="droppable">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              <Form.List name="lossReasons">
                {(fields, _, { errors }) => (
                  <>
                    {fields.map((field, index) => (
                      <Space align="baseline" key={field.key} style={{ display: "block" }}>
                        <Draggable draggableId={`${field.key}`} index={index}>
                          {(provided) => (
                            <div ref={provided.innerRef} {...provided.draggableProps}>
                              <Form.Item
                                className={cn(styles.marginBottom)}
                                key={field.key}
                                required={false}
                              >
                                <Row gutter={[layout.spacerS, layout.spacerS]}>
                                  <Col
                                    className={styles.greyIcons}
                                    span={1}
                                    {...provided.dragHandleProps}
                                  >
                                    <FontAwesomeIcon icon={faGripVertical} />
                                  </Col>
                                  <Col span={10}>
                                    <Form.Item
                                      className={styles.fullWidth}
                                      name={[index, "name"]}
                                      noStyle
                                      rules={getReasonNameRules(lossReasons, index)}
                                    >
                                      <TextField />
                                    </Form.Item>
                                  </Col>
                                  <Col span={10}>
                                    <Form.Item
                                      className={styles.fullWidth}
                                      name={[index, "funnelId"]}
                                      noStyle
                                      rules={[{ required: false }]}
                                    >
                                      {funnelField(index)}
                                    </Form.Item>
                                  </Col>
                                  <Col className={styles.greyIcons} span={1}>
                                    <Popconfirm
                                      cancelText={intl.formatMessage({
                                        id: "deals.setting.dealLostReasonModal.deletePopconfirm.cancel",
                                        defaultMessage: "Cancel",
                                        description: "Cancel button for Deal Lost Reason modal",
                                      })}
                                      description={intl.formatMessage({
                                        id: "deals.setting.dealLostReasonModal.deletePopconfirm.title",
                                        defaultMessage: "Are you sure to delete this reason?",
                                        description: "Title for Deal Lost Reason modal",
                                      })}
                                      okText={intl.formatMessage({
                                        id: "deals.setting.dealLostReasonModal.deletePopconfirm.ok",
                                        defaultMessage: "Delete",
                                        description: "Delete button for Deal Lost Reason modal",
                                      })}
                                      onConfirm={() => handleDeleteLossReason(index)}
                                      placement="left"
                                    >
                                      <FontAwesomeIcon icon={faTrash} />
                                    </Popconfirm>
                                  </Col>
                                </Row>
                              </Form.Item>
                            </div>
                          )}
                        </Draggable>
                      </Space>
                    ))}
                    <Form.Item className={cn({ [styles.formMargin]: dragging })}>
                      <Button onClick={handleAddLossReason} type="text">
                        {intl.formatMessage({
                          id: "deals.setting.dealLostReasonModal.addNewReason",
                          defaultMessage: "+ Add New Reason",
                          description: "Add new reason button in a Deal Lost Reason modal",
                        })}
                      </Button>
                      <Form.ErrorList errors={errors} />
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </div>
          )}
        </Droppable>
      </Form>
    </DragDropContext>
  );
};

const mapStateToProps = (state: RootState) => ({
  favoriteFunnelsIds: getFavoriteFunnels(state),
  funnels: getFunnels(state),
});

const mapDispatchToProps = {
  deleteDealLossReason: deleteDealLossReason.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(LossReasonsList);
